// The claim screen — reached at `/claim?code=…` (the camera shows a QR pointing here)
// or by typing a code. It has two modes (ADR-0060):
//
//   • Anonymous (no Session): the primary action is "Continue with Google" — a full-page
//     navigation to `/auth/login?returnTo=/claim?code=…`. Signing in with a valid code
//     is what CREATES the account (signup = a first Claim), then this screen approves.
//   • Signed in (member or a signed-in non-member): the existing "Claim this camera?
//     Name: ___" → `POST /api/claim/approve`. The camera flips to `claimed` on its next poll.
//
// Either way, on load the code is PRE-CHECKED (`GET /claim/check`, public) so a bad/expired
// code is caught BEFORE a Google round-trip or an approve — its slot on the screen is a
// clear message and proceeding is disabled. Approve outcomes are still surfaced explicitly
// (the Worker returns precise codes — cloud/src/lib/claim.ts): invalid (404), expired (410),
// already-claimed/used (409). The `code` is prefilled from the query but stays editable.

import { useEffect, useState } from "react";
import type { ApiClient, ClaimCheck } from "../api";
import { useClaim, type ClaimErrorReason } from "../useClaim";
import { normalizeClaimCode } from "../claimCode";
import { startLogin } from "../auth";

/** The typed pre-check reasons (a non-null `ClaimCheck.reason`). */
type PrecheckReason = NonNullable<ClaimCheck["reason"]>;

export interface ClaimScreenProps {
  client: ApiClient;
  /** The `?code=` value off the URL (the camera's QR). Prefills the field. */
  initialCode?: string;
  /** Navigate elsewhere (e.g. to the device manager after a successful claim). */
  onNavigate?: (to: string) => void;
  /** True when a Session exists (member or signed-in non-member) → show the approve form.
   *  False (anonymous) → show "Continue with Google". Defaults to signed-in. */
  signedIn?: boolean;
  /** Start Google sign-in with the given same-origin `returnTo` (full-page nav). Injected
   *  in tests; defaults to the real `/auth/login` navigation. */
  onLogin?: (returnTo: string) => void;
  /** Fired once the claim binds — signup = a first Claim, so the subject is now a member
   *  and the shell should refresh (so the done-state's member links resolve). */
  onClaimed?: () => void;
}

/** The owner-facing copy for each typed APPROVE failure reason. */
const ERROR_COPY: Record<ClaimErrorReason, { title: string; body: string }> = {
  invalid: {
    title: "That code isn’t valid",
    body: "We don’t recognise this Claim code. Double-check it on the camera’s screen, or rescan the QR.",
  },
  expired: {
    title: "That code has expired",
    body: "A Claim code is only good for about 10 minutes. On the camera, start claiming again to get a fresh code.",
  },
  taken: {
    title: "This camera is already claimed",
    body: "This code was already used. If it’s your camera, find it in your devices; otherwise it belongs to another account.",
  },
  unauthorized: {
    title: "Please sign in",
    body: "Your session expired. Sign in again to claim this camera.",
  },
  failed: {
    title: "Couldn’t claim the camera",
    body: "Something went wrong. Check your connection and try again.",
  },
};

/** Copy for each typed PRE-CHECK reason (a code that can't be approved before we even try). */
const PRECHECK_COPY: Record<PrecheckReason, { title: string; body: string }> = {
  unknown: {
    title: "We don’t recognise this code",
    body: "Check the code on your camera’s screen, or rescan the QR.",
  },
  expired: {
    title: "This code expired — check your camera",
    body: "Claim codes last about 10 minutes. On the camera, start claiming again for a fresh one.",
  },
  used: {
    title: "This code was already used",
    body: "Each Claim code works once. If this is your camera, it may already be in your account.",
  },
  rate_limited: {
    title: "Too many attempts",
    body: "Please wait a minute, then try the code again.",
  },
};

/** The pre-check's in-component state: not run, in flight, good, or a typed failure. */
type Precheck =
  | { state: "checking" }
  | { state: "ok" }
  | { state: "bad"; reason?: PrecheckReason }
  | null;

export function ClaimScreen({
  client,
  initialCode,
  onNavigate,
  signedIn = true,
  onLogin,
  onClaimed,
}: ClaimScreenProps): React.JSX.Element {
  const claim = useClaim(client);
  // Canonicalize the QR-prefilled code too, so a deep-link variant lands valid (#135).
  const [code, setCode] = useState(() => normalizeClaimCode(initialCode ?? ""));
  const [name, setName] = useState("");
  const [precheck, setPrecheck] = useState<Precheck>(null);

  // Signup = a first Claim: once the bind lands, the signed-in subject is a member, so
  // tell the shell to refresh (its done-state links go to member-only routes).
  useEffect(() => {
    if (claim.claimedDeviceId) onClaimed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once per successful bind
  }, [claim.claimedDeviceId]);

  // Pre-check the code (public GET) so a bad/expired one is caught before the Google
  // round-trip or an approve — but only once it's a COMPLETE code (8 alnum), so a
  // half-typed code doesn't flash "unknown". A network hiccup clears the state rather
  // than blocking; approve/the Worker re-validate regardless.
  useEffect(() => {
    const full = normalizeClaimCode(code);
    if (full.replace("-", "").length < 8) {
      setPrecheck(null);
      return;
    }
    let cancelled = false;
    setPrecheck({ state: "checking" });
    client
      .checkClaimCode(full)
      .then((r) => {
        if (!cancelled) setPrecheck(r.ok ? { state: "ok" } : { state: "bad", reason: r.reason });
      })
      .catch(() => {
        if (!cancelled) setPrecheck(null);
      });
    return () => {
      cancelled = true;
    };
  }, [code, client]);

  // Success: the claim is bound; the camera becomes `claimed` on its next poll.
  if (claim.claimedDeviceId) {
    return (
      <div className="claim" data-testid="claim-done" role="status">
        <div className="claim__art" aria-hidden="true">
          ✓
        </div>
        <h2 className="claim__title">Camera claimed</h2>
        <p className="claim__body">
          {name.trim()
            ? `“${name.trim()}” is now bound to your account.`
            : "This camera is now bound to your account."}{" "}
          It will finish connecting the next time it polls.
        </p>
        {/* Route the new owner onward instead of dead-ending: a Look is the next step. */}
        <div className="claim__done-actions">
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => onNavigate?.("/looks")}
            data-testid="claim-setup-look"
          >
            Set up a Look
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => onNavigate?.("/devices")}
          >
            View my cameras
          </button>
        </div>
      </div>
    );
  }

  const err = claim.error ? ERROR_COPY[claim.error.reason] : null;
  const bad = precheck?.state === "bad";
  const precheckMsg = bad ? PRECHECK_COPY[precheck.reason ?? "unknown"] : null;
  // The button gates on the code being COMPLETE (8 chars), not merely non-empty — a partial code
  // has no pre-check run yet, so a non-empty check alone would leave it enabled. A known-bad code
  // or an in-flight check also block it.
  const codeComplete = normalizeClaimCode(code).replace("-", "").length === 8;
  const blocked = !codeComplete || bad || precheck?.state === "checking";

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (blocked) return; // a bad/empty code never proceeds (also covers Enter on a disabled button)
    if (signedIn) {
      await claim.approve(code, name);
    } else {
      // Re-check right before spending a Google round-trip — the pre-check may not have resolved
      // yet (code just completed + clicked). A bad code blocks with the message; a /claim/check
      // outage FAILS OPEN (approve re-validates after sign-in, so an outage never blocks sign-in).
      if (precheck?.state !== "ok") {
        try {
          const r = await client.checkClaimCode(normalizeClaimCode(code));
          if (!r.ok) {
            setPrecheck({ state: "bad", reason: r.reason });
            return;
          }
        } catch {
          /* fail-open: /claim/check unreachable → proceed; approve is the real gate */
        }
      }
      // Carry the code through the OAuth round-trip: sign in, return to /claim?code=…,
      // then approve as a signed-in subject (creating the account if this is their first).
      (onLogin ?? startLogin)(`/claim?code=${normalizeClaimCode(code)}`);
    }
  }

  return (
    <div className="claim" data-testid="claim-screen">
      <h2 className="claim__title">{signedIn ? "Claim this camera?" : "Claim your camera"}</h2>
      {signedIn && (
        <p className="claim__body">
          Binding a camera to your account lets its Captures and Clips Sync to your gallery.
          Give it a name so you can tell your cameras apart.
        </p>
      )}

      {precheckMsg && (
        <div className="claim__error" role="alert" data-testid="claim-precheck-error">
          <strong>{precheckMsg.title}</strong>
          <span>{precheckMsg.body}</span>
        </div>
      )}

      {err && (
        <div className="claim__error" role="alert" data-testid="claim-error">
          <strong>{err.title}</strong>
          <span>{err.body}</span>
          {/* 409 "already claimed": if it's theirs, send them straight to the list. */}
          {claim.error?.reason === "taken" && (
            <button
              type="button"
              className="btn"
              onClick={() => onNavigate?.("/devices")}
              data-testid="claim-recover-devices"
            >
              Go to my cameras
            </button>
          )}
        </div>
      )}

      <form className="claim__form" onSubmit={handleSubmit} aria-busy={claim.submitting}>
        <label className="claim__label" htmlFor="claim-code">
          Claim code
        </label>
        <input
          id="claim-code"
          className="claim__input"
          name="code"
          value={code}
          onChange={(e) => {
            // Re-mask to the canonical XXXX-XXXX only when typing at the END (the common
            // case) so the auto-hyphen doesn't fight a mid-string edit + the caret; otherwise
            // keep the raw value, uppercased. Submit normalizes regardless (#135).
            const el = e.target;
            const atEnd = (el.selectionStart ?? el.value.length) >= el.value.length;
            setCode(atEnd ? normalizeClaimCode(el.value) : el.value.toUpperCase());
            if (claim.error) claim.reset();
          }}
          placeholder="e.g. WDJB-MJHT"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          aria-describedby="claim-code-help"
          disabled={claim.submitting}
          required
        />
        <p className="claim__help" id="claim-code-help">
          The code shows on your camera’s round screen when it first Syncs.
        </p>

        {/* The optional name is only meaningful once you can approve (signed in). */}
        {signedIn && (
          <details className="claim__name">
            <summary>Add a name (optional)</summary>
            <label className="claim__label" htmlFor="claim-name">
              Name
            </label>
            <input
              id="claim-name"
              className="claim__input"
              name="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Living room"
              maxLength={64}
              autoComplete="off"
              disabled={claim.submitting}
            />
          </details>
        )}

        {signedIn ? (
          <button
            type="submit"
            className="btn btn--primary claim__submit"
            disabled={claim.submitting || blocked}
          >
            {claim.submitting ? "Claiming…" : "Claim camera"}
          </button>
        ) : (
          <button
            type="submit"
            className="btn btn--primary claim__submit"
            data-testid="claim-google"
            disabled={blocked}
          >
            Continue with Google
          </button>
        )}
      </form>
    </div>
  );
}
